import type { FeatureKey } from '@/features/storefront/features';
import {
  SECTION_LIBRARY,
  createSection,
  isAnchorType,
  sectionSpec as sectionSpecFor,
  type AnchorSectionType,
  type Section,
  type SectionSpec,
  type SectionType,
} from '@/features/storefront/sections/schema';

/**
 * The storefront, as a list of pages a merchant may arrange.
 *
 * The home page was the first page to become data - a stack of sections in the
 * theme document rather than a component with copy in it - and this module is
 * that same idea for the rest of the shop. Every page below is described here
 * rather than implemented per page: its name, the one section that *is* it, and
 * what it renders before a merchant has touched it.
 *
 * Adding a page is meant to be an entry in {@link STOREFRONT_PAGES} plus an
 * anchor type in `sections/schema.ts` - not a new builder, a new editor tab or
 * a new save path. Nothing in this file knows which theme is being worn, and
 * nothing in it renders: a theme narrows what a page offers through its own
 * package (see `previewThemes/types.ts`), and the storefront page hands the
 * renderer its anchor's content as a slot.
 */

/**
 * Every customisable page, in the order the customiser lists them: the shop's
 * front door, then the way through the catalog, then the way out through the
 * till.
 */
export const PAGE_IDS = [
  'home',
  'product',
  'listing',
  'category',
  'search',
  'cart',
  'checkout',
  'login',
  'register',
  'account',
  'blog',
  'post',
  'notFound',
  'team_member',
] as const;

export type PageId = (typeof PAGE_IDS)[number];

/**
 * The shelves the page picker groups its pages under.
 *
 * A shop has more pages than a flat list stays readable at, and the grouping
 * is the shopper's journey rather than the codebase's: the front door, the way
 * through the catalog, the way out through the till.
 */
export const PAGE_GROUPS = [
  'home',
  'catalog',
  'product',
  'shopping',
  'customer',
  'content',
  'system',
] as const;

export type PageGroup = (typeof PAGE_GROUPS)[number];

export const PAGE_GROUP_LABELS: Record<PageGroup, string> = {
  home: 'Home',
  catalog: 'Catalog',
  product: 'Product',
  shopping: 'Shopping',
  customer: 'Customer',
  content: 'Content',
  system: 'System',
};

export interface PageSpec {
  id: PageId;
  /** What the page is called in the customiser's page picker. */
  label: string;
  /** Which shelf of the page picker it is listed under. */
  group: PageGroup;
  /** One line on what a shopper is doing when they are on it. */
  description: string;
  /**
   * The section that is the page's own content, or `null` for the home page -
   * which has no content of its own, and is therefore whatever the merchant
   * puts on it.
   */
  anchor: AnchorSectionType | null;
  /**
   * Where the page lives under the storefront root, for the "open this page"
   * link. A path with no id in it, because the customiser is designing the
   * page rather than one product.
   */
  path: string;
}

export const STOREFRONT_PAGES: readonly PageSpec[] = [
  {
    id: 'home',
    group: 'home',
    label: 'Home page',
    description: 'The front door. Everything on it is yours to arrange.',
    anchor: null,
    path: '/',
  },
  {
    id: 'product',
    group: 'product',
    label: 'Product page',
    description: 'One product: its pictures, its price and its buy button.',
    anchor: 'product_details',
    path: '/products',
  },
  {
    id: 'listing',
    group: 'catalog',
    label: 'Product listing',
    description: 'The catalog, and one category seen through a filter.',
    anchor: 'product_list',
    path: '/products',
  },
  {
    id: 'category',
    group: 'catalog',
    label: 'Category listing',
    description: 'The shelves of the shop, as a way in for a shopper who is browsing.',
    anchor: 'category_list',
    path: '/categories',
  },
  {
    id: 'search',
    group: 'catalog',
    label: 'Search results',
    description: 'What a shopper found, and the facets they narrow it by.',
    anchor: 'search_results',
    path: '/search',
  },
  {
    id: 'cart',
    group: 'shopping',
    label: 'Cart',
    description: 'The basket, on its way to the till.',
    anchor: 'cart_lines',
    path: '/cart',
  },
  {
    id: 'checkout',
    group: 'shopping',
    label: 'Checkout',
    description: 'Address, payment and the order summary. Add reassurance around it.',
    anchor: 'checkout_form',
    path: '/checkout',
  },
  {
    id: 'login',
    group: 'customer',
    label: 'Sign in',
    description: 'A returning shopper, on their way back to their orders.',
    anchor: 'customer_login',
    path: '/account/login',
  },
  {
    id: 'register',
    group: 'customer',
    label: 'Create an account',
    description: 'Where you make the case for keeping their details.',
    anchor: 'customer_register',
    path: '/account/register',
  },
  {
    id: 'account',
    group: 'customer',
    label: 'Account',
    description: 'A signed-in shopper\u2019s own page - orders, addresses, details.',
    anchor: 'customer_account',
    path: '/account',
  },
  {
    id: 'blog',
    group: 'content',
    label: 'Blog',
    description: 'Your writing, newest first.',
    anchor: 'blog_index',
    path: '/blogs/news',
  },
  {
    id: 'post',
    group: 'content',
    label: 'Blog post',
    description: 'One post. What stands under it is where readers become shoppers.',
    anchor: 'blog_post',
    path: '/blogs/news',
  },
  {
    // Appended to `PAGE_IDS` rather than slotted in beside the blog, because
    // that tuple is what the server validates a theme document against; it is
    // listed here, under Content, because that is where a merchant looks.
    id: 'team_member',
    group: 'content',
    label: 'Team member profile',
    description: 'One person: who they are, when they work, and a button to book them.',
    anchor: 'team_member_details',
    path: '/team',
  },
  {
    id: 'notFound',
    group: 'system',
    label: 'Page not found',
    description: 'A dead end. Offer a way back in rather than an apology.',
    anchor: 'not_found',
    path: '/this-page-does-not-exist',
  },
] as const;

export function isPageId(value: string): value is PageId {
  return (PAGE_IDS as readonly string[]).includes(value);
}

/** One page's description, falling back to the home page for an id we retired. */
export function pageSpec(id: string): PageSpec {
  return STOREFRONT_PAGES.find((spec) => spec.id === id) ?? STOREFRONT_PAGES[0];
}

/** The anchor type a page owns, or `null` for the home page. */
export function pageAnchorType(id: PageId): AnchorSectionType | null {
  return pageSpec(id).anchor;
}

/**
 * A new section with an id that does not change between renders.
 *
 * `createSection` generates a fresh id every call, which is right for the
 * builder - two sections a merchant added must not share an identity. It is
 * wrong for a default, because a default is recomputed whenever the theme
 * document's reference changes, and a section whose id changed is a section
 * React unmounts and mounts again. On the checkout page that would throw away
 * a half-filled address form on the first re-render.
 *
 * Derived from the type, which is unique within a page's defaults, and stable
 * across every recomputation of them.
 */
export function defaultSection(type: SectionType): Section {
  return { ...createSection(type), id: `default-${type}` };
}

/**
 * What a page renders before anyone has arranged it.
 *
 * For every page but the home page that is its anchor alone: the shop as it
 * has always looked, now with somewhere to add to. The home page keeps the two
 * sections a shop without a home page has always fallen back to.
 *
 * A theme may replace this - see `themePageDefaults` - which is how a theme
 * ships a product page that already carries its own promises strip.
 */
export function defaultPageSections(id: PageId): Section[] {
  if (id === 'home') return [defaultSection('featured_products'), defaultSection('categories')];
  const anchor = pageAnchorType(id);
  return anchor ? [defaultSection(anchor)] : [];
}

/**
 * The ceiling the server also enforces, per page rather than per theme: a
 * merchant who has built an elaborate home page has not thereby spent their
 * product page's budget.
 */
export const MAX_SECTIONS_PER_PAGE = 30;

/**
 * Whether a section type may be added to a page whose theme offers `available`.
 *
 * No anchor is ever addable, on any page - not merely the one it belongs to. A
 * cart dropped onto the product page would be a section drawn from a slot that
 * page does not fill, which renders as nothing at all. Everything else is
 * offered everywhere unless the theme has named a narrower set.
 */
export function isAddable(type: SectionType, available?: readonly SectionType[]): boolean {
  if (isAnchorType(type)) return false;
  return !available || available.includes(type);
}

/**
 * The "add section" palette for a page, in the order it is offered.
 *
 * The theme's order where it named one, and the shop's otherwise - a theme
 * that puts its lookbook gallery first on the product page has said something
 * about that page, and the palette should say it back.
 */
export function sectionPalette(available?: readonly SectionType[]): readonly SectionSpec[] {
  if (!available) return SECTION_LIBRARY.filter((spec) => isAddable(spec.type));
  return available
    .filter((type) => isAddable(type, available))
    .map((type) => sectionSpecFor(type));
}

/**
 * The pages, gathered onto their shelves and keeping the order they are
 * declared in. Empty groups are dropped rather than rendered as a heading over
 * nothing, so a build that retires a page does not leave a hole in the picker.
 */
export function groupedPages(): ReadonlyArray<{ group: PageGroup; pages: readonly PageSpec[] }> {
  return PAGE_GROUPS.map((group) => ({
    group,
    pages: STOREFRONT_PAGES.filter((spec) => spec.group === group),
  })).filter((shelf) => shelf.pages.length > 0);
}

/**
 * Which store feature a page needs in order to exist at all.
 *
 * One table, read by both halves that care: the router, which turns a
 * switched-off page into the shop's own 404, and the customiser, which marks
 * it in the picker.
 *
 * Note what this deliberately does *not* do - it does not remove anything from
 * `PAGE_IDS` or from `groupedPages()`. `PAGE_IDS` is a type: the server
 * validates a theme document against it and the content areas are built from
 * it, so making it depend on a store's settings would make a *theme's*
 * validity depend on a *shop's* configuration. And a merchant who arranged
 * their cart page still has those sections saved; dropping the page from the
 * picker would keep the work and take away the only way back to it.
 */
export const PAGE_FEATURE: Partial<Record<PageId, FeatureKey>> = {
  cart: 'cart',
  checkout: 'checkout',
  login: 'accounts',
  register: 'accounts',
  account: 'accounts',
  search: 'search',
  blog: 'blog',
  post: 'blog',
};
